import {
  IN_FLIGHT_PUBLISH_RECORDS_QUERY,
  PUBLISH_RECORDS_FOR_VIDEOS_QUERY,
} from '@src/gql/publish-record-gql';
import {
  IN_FLIGHT_PUBLISH_STATUSES,
  PublishStatus,
} from '@src/types.ts/publishRecord';
import { LogManager } from '@src/utility/Logger';

import type { Client } from '@datocms/cma-client-browser';
import type ApiClient from '@src/apiClient/ApiClient';
import type {
  CreatePublishRecordInput,
  PublishRecord,
  UpdatePublishRecordInput,
} from '@src/types.ts/publishRecord';
import type { GraphQLClient, RequestDocument } from 'graphql-request';

const logger = LogManager.getLogger('repository:PublishRecordRepository');

const PUBLISH_RECORD_MODEL_API_KEY = 'publish_record';
const PAGE_SIZE = 100;
/** Max concurrent record creates per fan-out — bounds latency without risking
 *  DatoCMS CMA rate limits. */
const CREATE_CONCURRENCY = 5;

/**
 * A JSON field arrives already parsed on the GraphQL (CDA) path and as a string
 * on the CMA path. Tolerate both and never throw on a malformed value.
 */
const parseJsonField = <T>(raw: unknown): T | null => {
  if (raw == null) return null;
  if (typeof raw === 'object') return raw as T;
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) as T;
    } catch {
      logger.warn('Unparseable JSON field on publish_record — defaulting null');
      return null;
    }
  }
  return null;
};

/** GraphQL record (camelCase); JSON fields parsed. `videoId`/`storyId` are
 * plain string ids (not Dato links). */
export type RawPublishRecord = {
  id: string;
  videoId: string;
  storyId: string;
  groupId: string;
  mirrorsToLegacy: boolean;
  language: string;
  aspectRatio: string;
  resolution: string;
  publishStatus: string;
  renderId: string | null;
  attempts: unknown;
  errorCode: string | null;
  errorMessage: string | null;
  renderStartedAt: string | null;
  renderCompletedAt: string | null;
  outputJson: unknown;
  snapshotRef: unknown;
  fingerprintsJson: unknown;
  publishedBy: string | null;
  publishedOn: string | null;
  renderSourceKey: string | null;
};

/** Map a GraphQL `publish_record` (raw, camelCase) to the domain `PublishRecord`.
 *  Exported so the real-time subscription maps its push the same way. */
export const mapPublishRecordFromGql = (
  raw: RawPublishRecord,
): PublishRecord => ({
  id: raw.id,
  videoId: raw.videoId,
  storyId: raw.storyId,
  groupId: raw.groupId,
  mirrorsToLegacy: Boolean(raw.mirrorsToLegacy),
  language: raw.language,
  aspectRatio: raw.aspectRatio,
  resolution: raw.resolution,
  publishStatus: (raw.publishStatus as PublishStatus) || PublishStatus.Queued,
  renderId: raw.renderId ?? null,
  attempts: parseJsonField<PublishRecord['attempts']>(raw.attempts) ?? [],
  errorCode: raw.errorCode ?? null,
  errorMessage: raw.errorMessage ?? null,
  renderStartedAt: raw.renderStartedAt ?? null,
  renderCompletedAt: raw.renderCompletedAt ?? null,
  outputJson: parseJsonField<PublishRecord['outputJson']>(raw.outputJson),
  snapshotRef: parseJsonField<PublishRecord['snapshotRef']>(raw.snapshotRef),
  fingerprintsJson: parseJsonField<PublishRecord['fingerprintsJson']>(
    raw.fingerprintsJson,
  ),
  publishedBy: raw.publishedBy ?? null,
  publishedAt: raw.publishedOn ?? null,
  renderSourceKey: raw.renderSourceKey ?? null,
});

/** JSON.stringify a value for a CMA JSON field, preserving null. */
const toJsonField = (value: unknown): string | null =>
  value == null ? null : JSON.stringify(value);

/**
 * CMA writes + GraphQL reads for the `publish_record` model. Pure Dato I/O — no
 * observable state (the in-flight tracking store and orchestration service sit
 * above it). Maps Dato item ↔ domain `PublishRecord`. The model has no
 * draft/published split (`draft_mode_active: false`), so records are live on
 * create/update — no `items.publish()` step.
 */
export class PublishRecordRepository {
  constructor(
    private readonly dClient: Client | ApiClient,
    private readonly gqlClient: GraphQLClient,
  ) {}

  /**
   * Create one target record per input (a publish command's fan-out), each
   * linking its own per-AR `video` and the shared `story`. Every record is its
   * own item, so concurrent creates never touch the Video record.
   */
  async createRecords(
    inputs: CreatePublishRecordInput[],
  ): Promise<{ id: string }[]> {
    if (inputs.length === 0) return [];
    const itemType = await this.dClient.itemTypes.find(
      PUBLISH_RECORD_MODEL_API_KEY,
    );
    const createOne = async (
      input: CreatePublishRecordInput,
    ): Promise<{ id: string }> => {
      const item = await this.dClient.items.create({
        item_type: { type: 'item_type', id: itemType.id },
        video_id: input.videoId,
        story_id: input.storyId,
        group_id: input.groupId,
        mirrors_to_legacy: input.mirrorsToLegacy,
        language: input.language,
        aspect_ratio: input.aspectRatio,
        resolution: input.resolution,
        publish_status: input.publishStatus ?? PublishStatus.Queued,
        render_id: null,
        attempts: null,
        error_code: null,
        error_message: null,
        render_started_at: null,
        render_completed_at: null,
        output_json: null,
        snapshot_ref: toJsonField(input.snapshotRef),
        fingerprints_json: toJsonField(input.fingerprintsJson),
        published_by: input.publishedBy ?? null,
        published_on: input.publishedAt ?? null,
        render_source_key: input.renderSourceKey ?? null,
      });
      return { id: item.id };
    };

    // Create in bounded-concurrency batches (order preserved — the caller maps
    // results back by index) so a fan-out doesn't pay N serial round-trips.
    const created: { id: string }[] = [];
    for (let i = 0; i < inputs.length; i += CREATE_CONCURRENCY) {
      const batch = inputs.slice(i, i + CREATE_CONCURRENCY);
      created.push(...(await Promise.all(batch.map(createOne))));
    }
    return created;
  }

  /**
   * Patch the mutable lifecycle fields of a single record as its render
   * progresses. Only the provided fields are written.
   */
  async updateRecord(
    id: string,
    patch: UpdatePublishRecordInput,
  ): Promise<void> {
    const payload: Record<string, unknown> = {};
    if (patch.publishStatus !== undefined)
      payload.publish_status = patch.publishStatus;
    if (patch.renderId !== undefined) payload.render_id = patch.renderId;
    if (patch.attempts !== undefined)
      payload.attempts = toJsonField(patch.attempts);
    if (patch.errorCode !== undefined) payload.error_code = patch.errorCode;
    if (patch.errorMessage !== undefined)
      payload.error_message = patch.errorMessage;
    if (patch.renderStartedAt !== undefined)
      payload.render_started_at = patch.renderStartedAt;
    if (patch.renderCompletedAt !== undefined)
      payload.render_completed_at = patch.renderCompletedAt;
    if (patch.outputJson !== undefined)
      payload.output_json = toJsonField(patch.outputJson);
    if (patch.fingerprintsJson !== undefined)
      payload.fingerprints_json = toJsonField(patch.fingerprintsJson);

    if (Object.keys(payload).length === 0) return;

    await this.dClient.items.update(id, payload);
  }

  /**
   * All publish records for a clip's Video record set (main + associated
   * per-AR ids), newest first. Paginated — a clip's history is unbounded.
   */
  async getRecordsForVideos(videoIds: string[]): Promise<PublishRecord[]> {
    const ids = Array.from(new Set(videoIds.filter(Boolean)));
    if (ids.length === 0) return [];
    return this.paginate(PUBLISH_RECORDS_FOR_VIDEOS_QUERY, { videoIds: ids });
  }

  /**
   * In-flight records for one story (seed for live tracking on load/refresh).
   * The persisted status is the source of truth, so progress survives a
   * refresh.
   */
  async getInFlightForStory(storyId: string): Promise<PublishRecord[]> {
    if (!storyId) return [];
    return this.paginate(IN_FLIGHT_PUBLISH_RECORDS_QUERY, {
      storyId,
      statuses: IN_FLIGHT_PUBLISH_STATUSES,
    });
  }

  private async paginate(
    query: RequestDocument,
    variables: Record<string, unknown>,
  ): Promise<PublishRecord[]> {
    const all: RawPublishRecord[] = [];
    let skip = 0;
    for (;;) {
      const resp = await this.gqlClient.request<{
        allPublishRecords: RawPublishRecord[];
      }>(query, { ...variables, first: PAGE_SIZE, skip });
      const batch = resp.allPublishRecords ?? [];
      all.push(...batch);
      if (batch.length < PAGE_SIZE) break;
      skip += PAGE_SIZE;
    }
    return all.map(mapPublishRecordFromGql);
  }
}
